import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import LoadingCard from "components/LoadingCard/LoadingCard";
import RichTextEditor from "components/RichTextEditor/RichTextEditor";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { uploadImage } from "pages/MarketingPages/pagesApi";
import { uploadDataImages } from "./bodyImages";
import { isValidSlug, slugify } from "./slug";
import {
  createPost,
  errorMessage,
  fetchCategories,
  fetchPost,
  setPostStatus,
  updatePost,
} from "./postsApi";

const { Title, Text } = Typography;

const STATUS_LABEL = { published: "Публикувана", draft: "Чернова" };

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  coverImage: { url: "", alt: "" },
  categories: [],
  authorName: "",
  seo: { title: "", description: "", ogImage: "", noindex: false },
};

/**
 * The editor already normalizes its empty document to "", but a body stored by
 * an earlier editor can still be the empty paragraph it used to emit.
 */
const isEmptyBody = (html) => !html || html === "<p><br></p>" || html === "<p></p>";

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ marginBottom: 6 }}>
      <Text strong>{label}</Text>
      {hint && (
        <>
          {" "}
          <Text type="secondary">{hint}</Text>
        </>
      )}
    </div>
    {children}
  </div>
);

/**
 * Create or edit one post, in one language.
 *
 * A post is one document per language, so this edits a single language variant.
 * `translationGroup` is what pairs it with its other languages — passed in when
 * creating a translation of an existing post, and never shown as an input.
 */
export default function PostEditor({ postId, lang, translationGroup, onBack, onSaved }) {
  const isNew = !postId;
  const [values, setValues] = useState(EMPTY);
  const [post, setPost] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isBusy, setIsBusy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Once the slug has been set deliberately it stops tracking the title —
  // a published slug is a URL, and silently rewriting it as someone edits a
  // typo in the title would break the link.
  const [isSlugLocked, setIsSlugLocked] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [categoryList, loaded] = await Promise.all([
        fetchCategories(lang),
        postId ? fetchPost(postId) : Promise.resolve(null),
      ]);
      setCategories(categoryList || []);
      if (loaded) {
        setPost(loaded.post);
        setSiblings(loaded.siblings || []);
        setValues({
          ...EMPTY,
          ...loaded.post,
          coverImage: { ...EMPTY.coverImage, ...(loaded.post.coverImage || {}) },
          seo: { ...EMPTY.seo, ...(loaded.post.seo || {}) },
          categories: (loaded.post.categories || []).map((category) =>
            typeof category === "string" ? category : category._id
          ),
        });
      }
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [postId, lang]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (field) => (value) => setValues((current) => ({ ...current, [field]: value }));

  const changeTitle = (event) => {
    const title = event.target.value;
    setValues((current) => ({
      ...current,
      title,
      slug: isSlugLocked ? current.slug : slugify(title),
    }));
  };

  const changeSlug = (event) => {
    setIsSlugLocked(true);
    setValues((current) => ({ ...current, slug: event.target.value }));
  };

  const uploadCover = async (file) => {
    setIsUploading(true);
    try {
      const url = await uploadImage(file);
      if (!url) throw new Error("Качването не върна URL адрес");
      change("coverImage")({ ...values.coverImage, url });
    } catch (error) {
      message.error(`Неуспешно качване: ${errorMessage(error)}`);
    } finally {
      setIsUploading(false);
    }
    return false;
  };

  /** What the API accepts — `status` is rejected here, publishing is separate. */
  const payload = () => ({
    title: values.title.trim(),
    slug: values.slug.trim(),
    excerpt: values.excerpt || "",
    body: values.body,
    coverImage: values.coverImage,
    categories: values.categories,
    authorName: values.authorName || "",
    seo: values.seo,
    ...(isNew && { lang, ...(translationGroup && { translationGroup }) }),
  });

  const validate = () => {
    if (!values.title.trim()) return "Статията се нуждае от заглавие";
    if (!isValidSlug(values.slug.trim()))
      return "Адресът (slug) трябва да е с малки латински букви, разделени с тире";
    if (isEmptyBody(values.body)) return "Статията се нуждае от съдържание";
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      message.error(problem);
      return null;
    }
    // Pasted images live in the editor as base64, which the API sanitizer
    // drops by scheme — upload them and swap in the served URLs before the
    // body leaves the browser. A failed upload aborts the save loudly.
    const body = await uploadDataImages(values.body);
    if (body !== values.body) change("body")(body);
    const saved = isNew
      ? await createPost({ ...payload(), body })
      : await updatePost(postId, { ...payload(), body });
    setPost(saved);
    return saved;
  };

  const onSave = async () => {
    setIsBusy(true);
    try {
      const saved = await save();
      if (saved) {
        message.success("Запазено");
        onSaved?.(saved);
      }
    } catch (error) {
      message.error(`Неуспешно запазване: ${errorMessage(error)}`);
    } finally {
      setIsBusy(false);
    }
  };

  const onTogglePublished = async () => {
    setIsBusy(true);
    try {
      // Publish acts on what is stored, so an unsaved form would publish the
      // previous text — same rule as the marketing pages editor.
      const saved = await save();
      if (!saved) return;
      const next = post?.status === "published" ? "draft" : "published";
      setPost(await setPostStatus(saved._id, next));
      message.success(next === "published" ? "Публикувана" : "Свалена от сайта");
      onSaved?.(saved);
    } catch (error) {
      message.error(`Неуспешна промяна на статуса: ${errorMessage(error)}`);
    } finally {
      setIsBusy(false);
    }
  };

  if (isLoading) return <LoadingCard />;

  if (loadError) {
    return (
      <div style={{ padding: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 16 }}>
          Назад
        </Button>
        <Alert type="error" showIcon message="Неуспешно отваряне на статията" description={loadError} />
      </div>
    );
  }

  const isPublished = post?.status === "published";

  return (
    <div style={{ padding: 16, paddingBottom: 96, maxWidth: 760, margin: "0 auto" }}>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 12 }}>
        Назад
      </Button>

      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          {isNew ? "Нова статия" : values.title || "Статия"}
        </Title>
        <Space wrap size={8}>
          <Tag color={lang === "bg" ? "blue" : "purple"}>{lang.toUpperCase()}</Tag>
          {!isNew && (
            <Tag color={isPublished ? "green" : "default"}>
              {isPublished ? STATUS_LABEL.published : STATUS_LABEL.draft}
            </Tag>
          )}
          {translationGroup && isNew && <Tag color="gold">Превод</Tag>}
          {siblings.map((sibling) => (
            <Tag key={sibling._id}>
              {sibling.lang.toUpperCase()}: {STATUS_LABEL[sibling.status] || sibling.status}
            </Tag>
          ))}
        </Space>
      </Space>

      <Field label="Заглавие">
        <Input
          value={values.title}
          onChange={changeTitle}
          style={CONTROL}
          maxLength={300}
          placeholder="Заглавие на статията"
        />
      </Field>

      <Field label="Адрес (slug)" hint="— адресът на сайта, само на латиница">
        <Input
          value={values.slug}
          onChange={changeSlug}
          style={CONTROL}
          maxLength={200}
          status={values.slug && !isValidSlug(values.slug) ? "error" : undefined}
          placeholder="zaglavie-na-statiyata"
        />
        {!isSlugLocked && (
          <Text type="secondary">Генерира се от заглавието, докато не го редактираш.</Text>
        )}
      </Field>

      <Field label="Резюме" hint="— кратко описание в списъците">
        <Input.TextArea
          value={values.excerpt}
          onChange={(event) => change("excerpt")(event.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={2000}
        />
      </Field>

      <Field label="Съдържание">
        <RichTextEditor value={values.body || ""} onChange={change("body")} />
      </Field>

      <Field label="Категории">
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          value={values.categories}
          onChange={change("categories")}
          placeholder="Няма категории"
          options={categories.map((category) => ({
            value: category._id,
            label: category.name,
          }))}
        />
      </Field>

      <Field label="Снимка за корица">
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          {values.coverImage.url && (
            <img
              src={values.coverImage.url}
              alt={values.coverImage.alt || ""}
              style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
            />
          )}
          <Space wrap>
            <Upload beforeUpload={uploadCover} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={isUploading} style={CONTROL}>
                {values.coverImage.url ? "Смени изображението" : "Качи изображение"}
              </Button>
            </Upload>
            {values.coverImage.url && (
              <Button
                type="link"
                onClick={() => change("coverImage")({ ...values.coverImage, url: "" })}
              >
                Премахни
              </Button>
            )}
          </Space>
          <Input
            value={values.coverImage.alt}
            onChange={(event) =>
              change("coverImage")({ ...values.coverImage, alt: event.target.value })
            }
            placeholder="Алтернативен текст"
            style={CONTROL}
            maxLength={500}
          />
        </Space>
      </Field>

      <Collapse
        style={{ marginBottom: 16 }}
        items={[
          {
            key: "seo",
            label: "SEO и автор",
            children: (
              <>
                <Field label="SEO заглавие" hint="— по подразбиране е заглавието на статията">
                  <Input
                    value={values.seo.title}
                    onChange={(event) =>
                      change("seo")({ ...values.seo, title: event.target.value })
                    }
                    style={CONTROL}
                    maxLength={200}
                    placeholder={values.title}
                  />
                </Field>
                <Field label="SEO описание" hint="— по подразбиране е резюмето">
                  <Input.TextArea
                    value={values.seo.description}
                    onChange={(event) =>
                      change("seo")({ ...values.seo, description: event.target.value })
                    }
                    autoSize={{ minRows: 2, maxRows: 5 }}
                    maxLength={500}
                    placeholder={values.excerpt}
                  />
                </Field>
                <Field label="Име на автора">
                  <Input
                    value={values.authorName}
                    onChange={(event) => change("authorName")(event.target.value)}
                    style={CONTROL}
                    maxLength={200}
                  />
                </Field>
                <Checkbox
                  checked={values.seo.noindex}
                  onChange={(event) =>
                    change("seo")({ ...values.seo, noindex: event.target.checked })
                  }
                >
                  Скрий тази статия от търсачките
                </Checkbox>
              </>
            ),
          },
        ]}
      />

      <Card
        size="small"
        style={{
          position: "sticky",
          bottom: 0,
          borderRadius: 8,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onSave} disabled={isBusy} loading={isBusy} style={CONTROL}>
            Запази
          </Button>
          <Button
            type={isPublished ? "default" : "primary"}
            danger={isPublished}
            onClick={onTogglePublished}
            disabled={isBusy}
            loading={isBusy}
            style={CONTROL}
          >
            {isPublished ? "Свали от сайта" : "Запази и публикувай"}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
